import { Utils } from "../utils/utils";
import DragDropDroppableOptions from "../interfaces/DragDropDroppableOptions";
import DragDropElementHost from "../interfaces/DragDropElementHost";
import DragDropUIData from "../interfaces/DragDropUIData";
import HTMLElementExtendOptions from "../interfaces/HTMLElementExtendOptions";
import { DragDropBase } from "./DragDropBase";
import DragDropDraggable from "./DragDropDraggable";
import DragDropManager from './DragDropManager';
import { isTouch, pointerenter, pointerleave } from "../utils/touchUtils";

/**
 * DragDropDroppable class
 */
export default class DragDropDroppable extends DragDropBase implements HTMLElementExtendOptions<DragDropDroppableOptions>
{
  public accept: (el: HTMLElement) => boolean;

  /**
   * Constructor
   * @param el 
   * @param option 
   */
  constructor (public el: HTMLElement, public option: DragDropDroppableOptions = {})
  {
    super();
    // create var event binding so we can easily remove and still look like TS methods (unlike anonymous functions)
    this._mouseEnter = this._mouseEnter.bind(this);
    this._mouseLeave = this._mouseLeave.bind(this);
    this.enable();
    this._setupAccept();
  }

  /**
   * Add an event listener
   * @param event The event to listen to
   * @param callback The callback to call when the event is triggered
   */
  public on (event: 'drop' | 'dropover' | 'dropout', callback: (event: DragEvent) => void): void
  {
    super.on(event, callback);
  }

  /**
   * Remove an event listener
   * @param event The event to remove the listener from
   */
  public off (event: 'drop' | 'dropover' | 'dropout'): void
  {
    super.off(event);
  }

  /**
   * Enable the droppable
   */
  public enable (): void
  {
    if (this.disabled === false)
      return;

    super.enable();
    this.el.classList.add('ui-droppable');
    this.el.classList.remove('ui-droppable-disabled');
    this.el.addEventListener('mouseenter', this._mouseEnter);
    this.el.addEventListener('mouseleave', this._mouseLeave);
    if (isTouch)
    {
      this.el.addEventListener('pointerenter', pointerenter);
      this.el.addEventListener('pointerleave', pointerleave);
    }
  }

  /**
   * Disable the droppable
   * @param forDestroy True if we're disabling for destroy
   */
  public disable (forDestroy = false): void
  {
    if (this.disabled === true) 
      return;

    super.disable();
    this.el.classList.remove('ui-droppable');
    if (!forDestroy) 
      this.el.classList.add('ui-droppable-disabled');

    this.el.removeEventListener('mouseenter', this._mouseEnter);
    this.el.removeEventListener('mouseleave', this._mouseLeave);
    if (isTouch)
    {
      this.el.removeEventListener('pointerenter', pointerenter);
      this.el.removeEventListener('pointerleave', pointerleave);
    }
  }

  /**
   * Destroy the droppable
   */
  public destroy (): void
  {
    this.disable(true);
    this.el.classList.remove('ui-droppable');
    this.el.classList.remove('ui-droppable-disabled');
    super.destroy();
  }

  /**
   * Update the options
   * @param opts The options to update
   * @returns {DragDropDroppable}
   */
  public updateOption (opts: DragDropDroppableOptions): DragDropDroppable
  {
    Object.keys(opts).forEach(key => this.option[ key ] = opts[ key ]);
    this._setupAccept();
    return this;
  }

  /** 
   * @internal 
   * Called when the cursor enters our area - prepare for a possible drop and track leaving 
   */
  protected _mouseEnter (e: MouseEvent): void
  {
    if (!DragDropManager.dragElement) 
      return;

    if (!this._canDrop(DragDropManager.dragElement.el)) 
      return;

    e.preventDefault();
    e.stopPropagation();

    // make sure when we enter this, that the last one gets a leave FIRST to correctly cleanup as we don't always do
    if (DragDropManager.dropElement && DragDropManager.dropElement !== this)
    {
      DragDropManager.dropElement._mouseLeave(e as DragEvent, true);
    }
    DragDropManager.dropElement = this;

    const ev = Utils.initEvent<DragEvent>(e, { target: this.el, type: 'dropover' });
    if (this.option.over)
    {
      this.option.over(ev, this._ui(DragDropManager.dragElement));
    }
    this.triggerEvent('dropover', ev);
    this.el.classList.add('ui-droppable-over');
  }

  /** 
   * @internal 
   * Called when the item is leaving our area, stop tracking if we had moving item 
   * @param {MouseEvent} e The mouse event
   * @param {boolean} calledByEnter True if this was called by the enter event
   */
  protected _mouseLeave (e: MouseEvent, calledByEnter = false): void
  {
    if (!DragDropManager.dragElement || DragDropManager.dropElement !== this) 
      return;

    e.preventDefault();
    e.stopPropagation();

    const ev = Utils.initEvent<DragEvent>(e, { target: this.el, type: 'dropout' });
    if (this.option.out)
    {
      this.option.out(ev, this._ui(DragDropManager.dragElement));
    }
    this.triggerEvent('dropout', ev);

    if (DragDropManager.dropElement === this)
    {
      delete DragDropManager.dropElement;

      // if we're still over a parent droppable, send it an enter as we don't get one from leaving nested children
      if (!calledByEnter)
      {
        let parentDrop: DragDropDroppable;
        let parent: DragDropElementHost = this.el.parentElement;
        while (!parentDrop && parent)
        {
          parentDrop = parent.ddElement?.ddDroppable;
          parent = parent.parentElement;
        }

        if (parentDrop)
        {
          parentDrop._mouseEnter(e);
        }
      }
    }
  }

  /** 
   * Item is being dropped on us - called by the drag mouseup handler - this calls the client drop event 
   * @param {MouseEvent} e The mouse event
   */
  public drop (e: MouseEvent): void
  {
    e.preventDefault();
    const ev = Utils.initEvent<DragEvent>(e, { target: this.el, type: 'drop' });
    if (this.option.drop)
    {
      this.option.drop(ev, this._ui(DragDropManager.dragElement));
    }
    this.triggerEvent('drop', ev);
  }

  /** 
   * @internal 
   * True if element matches the string/method accept option 
   * @param {HTMLElement} el The element to check
   * @returns {boolean}
   */
  protected _canDrop (el: HTMLElement): boolean
  {
    return el && (!this.accept || this.accept(el));
  }

  /** 
   * @internal 
   * Setup the accept function
   * @returns {DragDropDroppable}
   */
  protected _setupAccept (): DragDropDroppable
  {
    if (!this.option.accept) 
      return this;

    if (typeof this.option.accept === 'string')
    {
      this.accept = (el: HTMLElement) => el.classList.contains(this.option.accept as string) || el.matches(this.option.accept as string);
    } else
    {
      this.accept = this.option.accept;
    }
    return this;
  }

  /** 
   * @internal 
   * Get the UI data for the drop event
   * @param {DragDropDraggable} drag The draggable object
   * @returns {DragDropUIData}
   */
  protected _ui (drag: DragDropDraggable): DragDropUIData
  {
    return {
      draggable: drag.el,
      ...drag.ui()
    };
  }
}
